const PREFIX = '@@ls.';

// Strip a leftover disk prefix so get/set never send @@ls.@@ls.* over IPC.
const toLogicalKey = (key: string): string =>
  key.startsWith(PREFIX) ? key.slice(PREFIX.length) : key;

// Older mains return the whole store object; newer mains return string[].
// Per rule: architecture-principles (ai.knowledge/global) — normalize both.
const normalizeKeyList = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.filter((key): key is string => typeof key === 'string').map(toLogicalKey);
  }
  if (raw && typeof raw === 'object') {
    // The legacy object is the WHOLE electron-store file, so it also carries
    // main-process internals (e.g. machineId) that were never renderer
    // preferences. Filter to @@ls.* like the web adapter and ipc.ts do.
    return Object.keys(raw)
      .filter((key) => key.startsWith(PREFIX))
      .map(toLogicalKey);
  }
  return [];
};

let cache: Record<string, string> = {};

// Read fence. A cache-miss get() crosses IPC, and a local set/remove/clear can
// land before its reply. Without a fence a late reply re-inserted a value
// that remove()/clear() had just dropped (the old guard only noticed a
// concurrent set, because set leaves a cache entry and remove leaves a gap).
// Every local mutation of a key bumps that key's revision; clear() bumps the
// store-wide generation. A read may populate the cache only when both are
// unchanged at completion. Per rule: think-in-systems (ai.knowledge/global) —
// one fence for set, remove, clear and rejected writes, not a per-op guard.
const revisions = new Map<string, number>();
let clearGeneration = 0;

const revisionOf = (logicalKey: string): number => revisions.get(logicalKey) ?? 0;

const bumpRevision = (logicalKey: string): number => {
  const next = revisionOf(logicalKey) + 1;
  revisions.set(logicalKey, next);
  return next;
};

export const get = async (key: string): Promise<string | null> => {
  const logicalKey = toLogicalKey(key);

  // The cache holds this renderer's latest local write, so a hit wins.
  if (cache[logicalKey] !== undefined) {
    return cache[logicalKey] ?? null;
  }

  const revision = revisionOf(logicalKey);
  const generation = clearGeneration;

  // Cache miss - fetch from electron-store via IPC
  const value = await window.electron?.ipcRenderer?.invoke('storage:getItem', logicalKey);

  if (revisionOf(logicalKey) !== revision || clearGeneration !== generation) {
    // The key changed locally while this reply was in flight, so the reply
    // describes a state the caller has already moved past. Answer from the
    // current state instead: a newer set() is served from cache, and after a
    // remove/clear (or a rolled-back set) a fresh read is issued. Main runs
    // the storage handlers synchronously in receipt order, so that read
    // observes every mutation this renderer sent before it. Callers such as
    // useLastRoomId apply the result to UI state, so returning the stale
    // value would undo the user's removal there too. Each retry needs a new
    // local mutation during the read, so this cannot spin on its own.
    return get(logicalKey);
  }

  if (value !== undefined && value !== null) {
    cache[logicalKey] = value;
  }
  return cache[logicalKey] ?? null;
};

export const set = async (key: string, value: string): Promise<void> => {
  const logicalKey = toLogicalKey(key);

  // Update cache immediately (synchronous) so reads see the write at once.
  cache[logicalKey] = value;
  const revision = bumpRevision(logicalKey);
  const generation = clearGeneration;

  // Persist to disk via IPC (asynchronous)
  try {
    await window.electron?.ipcRenderer?.invoke('storage:setItem', { key: logicalKey, value });
  } catch (error) {
    // Main never stored this value, so the cache must not keep claiming it.
    // Roll back only while this write is still the newest local change to
    // the key: a later set/remove/clear already owns the cache entry. The
    // rollback is a mutation too, so it bumps the revision; reads in flight
    // re-read from disk rather than trusting a pre-write reply. Later reads
    // start from the new revision, so nothing stays blocked.
    if (revisionOf(logicalKey) === revision && clearGeneration === generation) {
      delete cache[logicalKey];
      bumpRevision(logicalKey);
    }
    throw error;
  }
};

export const remove = (key: string): Promise<void> => {
  const logicalKey = toLogicalKey(key);
  delete cache[logicalKey];
  bumpRevision(logicalKey);
  return window.electron?.ipcRenderer?.invoke('storage:removeItem', logicalKey);
};

export const clear = (): Promise<void> => {
  cache = {};
  clearGeneration += 1;
  return window.electron?.ipcRenderer?.invoke('storage:clear');
};

export const keys = async (): Promise<string[]> => {
  const raw = await window.electron?.ipcRenderer?.invoke('storage:keys');
  return normalizeKeyList(raw);
};
